import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminImagePicker from '../AdminImagePicker';

/**
 * BlogPostEditor — granular control over every element of a blog post.
 *
 * Layout:
 *   • LEFT  (40%) : the form (boarding-pass fields, sections, phrases, easter eggs, CTA)
 *   • RIGHT (60%) : live-preview iframe (/blog/{slug}?preview=1) — reloads on save
 *
 * Every visual section has a show/hide toggle so admin can mix/match per post.
 */
const EMPTY_POST = {
  slug: '',
  title: '',
  excerpt: '',
  category_slug: '',
  author: 'DarijaPro Team',
  author_avatar_url: '',
  show_meta_strip: true,
  from_city: 'New York',
  to_city: 'Casablanca',
  flight_no: 'DPR-001',
  seat: '12A',
  gate: 'GATE 4',
  peek_mascot: '🐪',
  cover_image_url: '',
  cover_caption: '',
  cover_visible: true,
  intro_image_url: '',
  card_image_url: '',
  mascot_bubble_text: '',
  mascot_signature: '— Youssef ✈',
  mascot_visible: true,
  section_intro: { heading: '', body_md: '', visible: true },
  section_why_these: { heading: '', body_md: '', visible: true },
  section_mid_story: { heading: '', body_md: '', visible: true },
  section_cultural_notes: { heading: '', body_md: '', visible: true },
  section_conclusion: { heading: '', body_md: '', visible: true },
  inline_image: { url: '', caption: '', visible: true },
  pull_quote: { text: '', attribution: '', visible: true },
  easter_egg_1: { icon: '🫖', text: '', visible: true },
  easter_egg_2: { icon: '👨‍✈️', text: '', visible: true },
  phrases: [],
  cta: {
    label: '↓ KEEP LEARNING ↓',
    headline: 'Want to learn more?',
    subtitle: 'Log in to DarijaPro and keep going — happy learning.',
    button_label: 'LOG IN TO DARIJAPRO →',
    button_url: 'https://darijapro.com',
    visible: true,
  },
  credits: {
    heading: 'Credits & attributions',
    body_md: '',
    visible: true,
  },
  meta_description: '',
  meta_keywords: '',
  is_published: false,
};

export default function BlogPostEditor({ API, adminToken, isDarkMode, showNotification, slug, onClose }) {
  const isNew = !slug;
  const [p, setP] = useState(EMPTY_POST);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [currentSlug, setCurrentSlug] = useState(slug);  // updates if user renames
  const originalSlug = useRef(slug || '');               // ground truth captured at load — only rename if form slug differs from THIS
  const previewRef = useRef(null);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // 'cover' | 'inline' | 'avatar' | null
  const authHeaders = { Authorization: `Bearer ${adminToken}` };

  const headers = { Authorization: `Bearer ${adminToken}` };

  // Load categories + post
  useEffect(() => {
    fetch(`${API}/blog/admin/categories`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setCats);
    if (!isNew) {
      fetch(`${API}/blog/admin/posts/${slug}`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(doc => {
          if (doc) {
            setP({ ...EMPTY_POST, ...doc });
            // Capture the slug we LOADED as the ground truth for rename detection.
            // Only when the user changes the form slug AWAY from this do we send a rename request.
            originalSlug.current = doc.slug || slug;
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      originalSlug.current = '';
      setLoading(false);
    }
  }, [slug]); // eslint-disable-line

  // Field setters
  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));
  const setNested = (k, field, v) => setP(prev => ({ ...prev, [k]: { ...prev[k], [field]: v } }));
  const setPhrase = (i, field, v) => {
    const phrases = [...(p.phrases || [])];
    phrases[i] = { ...phrases[i], [field]: v };
    set('phrases', phrases);
  };
  const addPhrase = () => set('phrases', [...(p.phrases || []), { english: '', latin: '', arabic: '', note: '', visible: true }]);
  const removePhrase = (i) => {
    const phrases = [...(p.phrases || [])];
    phrases.splice(i, 1);
    set('phrases', phrases);
  };
  const movePhrase = (i, dir) => {
    const phrases = [...(p.phrases || [])];
    const j = i + dir;
    if (j < 0 || j >= phrases.length) return;
    [phrases[i], phrases[j]] = [phrases[j], phrases[i]];
    set('phrases', phrases);
  };

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const body = { ...p };
      // POST (create) only when no slug exists yet. After the first successful create
      // currentSlug is populated — every save after that becomes a PUT (update).
      // (Bug fix: previously checked isNew, which is set once on mount and never
      //  updates, so a second save in the same editor session tried to create again
      //  and hit "slug already exists".)
      if (!currentSlug) {
        const res = await fetch(`${API}/blog/admin/posts`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const result = await res.json();
          setCurrentSlug(result.slug);
          showNotification?.('Post created', 'success');
          // reload iframe
          setTimeout(() => { if (previewRef.current) previewRef.current.src = `/blog/${result.slug}?preview=1&t=${Date.now()}`; }, 200);
        } else {
          const err = await res.json().catch(() => ({}));
          showNotification?.(`Create failed: ${err.detail || res.statusText}`, 'error');
        }
      } else {
        // Only treat this as a rename when the form slug genuinely differs from the slug we LOADED
        // (originalSlug.current). currentSlug might lag during edit cycles, but originalSlug is stable.
        const wantsRename = !!body.slug && body.slug !== originalSlug.current;
        const renamePayload = wantsRename ? { ...body, new_slug: body.slug } : body;
        const res = await fetch(`${API}/blog/admin/posts/${currentSlug}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(renamePayload),
        });
        if (res.ok) {
          const result = await res.json();
          if (result.slug && result.slug !== currentSlug) {
            setCurrentSlug(result.slug);
            originalSlug.current = result.slug;   // future saves compare against the NEW slug
          }
          showNotification?.('Saved', 'success');
          setTimeout(() => { if (previewRef.current) previewRef.current.src = `/blog/${result.slug || currentSlug}?preview=1&t=${Date.now()}`; }, 200);
        } else {
          const err = await res.json().catch(() => ({}));
          showNotification?.(`Save failed: ${err.detail || res.statusText}`, 'error');
        }
      }
    } finally {
      setSaving(false);
    }
  }, [p, isNew, currentSlug, API, adminToken]); // eslint-disable-line

  // Theme classes
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inpBg = isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = isDarkMode ? 'bg-gray-900/30' : 'bg-gray-50';

  if (loading) return <div className={`text-center py-10 ${muted}`}>Loading post…</div>;

  return (
    <div className="-m-6">  {/* break out of parent padding so split fills nicely */}
      {/* Sticky toolbar */}
      <div className={`sticky top-0 z-10 flex items-center justify-between px-6 py-3 border-b ${border} ${isDarkMode ? 'bg-gray-800/95' : 'bg-white/95'} backdrop-blur`}>
        <div className="flex items-center gap-3">
          <button onClick={onClose} className={`text-sm ${muted} hover:underline`}>← All posts</button>
          <span className={`text-xs ${muted}`}>·</span>
          <span className="text-sm font-bold">{isNew ? 'New post' : `Editing: ${currentSlug}`}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!p.is_published} onChange={e => set('is_published', e.target.checked)} className="w-4 h-4" />
            <span>Published</span>
          </label>
          {currentSlug && (
            <a href={`/blog/${currentSlug}${p.is_published ? '' : '?preview=1'}`} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline">↗ Open</a>
          )}
          <button
            onClick={save}
            disabled={saving || !p.title}
            className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold"
          >
            {saving ? 'Saving…' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Split: form (left) + live preview (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0" style={{ minHeight: '85vh' }}>
        {/* ── FORM ── */}
        <div className={`lg:col-span-2 p-6 overflow-y-auto ${border} lg:border-r`} style={{ maxHeight: '85vh' }}>
          {/* Identity */}
          <Section title="Identity" cardBg={cardBg} border={border}>
            <Field label="Title" v={p.title} onChange={v => set('title', v)} inpBg={inpBg} />
            <Field label="Slug (URL, auto from title if blank)" v={p.slug} onChange={v => set('slug', v)} inpBg={inpBg} mono />
            <Field label="Excerpt (shown on list card)" v={p.excerpt} onChange={v => set('excerpt', v)} inpBg={inpBg} multiline />
            <Field label="Author" v={p.author} onChange={v => set('author', v)} inpBg={inpBg} />
            {/* Category — own row, prominent, with a visible warning when blank */}
            <div className={`mt-2 mb-3 p-3 rounded-lg border ${p.category_slug ? (isDarkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50') : 'border-amber-500/40 bg-amber-500/5'}`}>
              <label className="text-sm font-extrabold mb-1.5 flex items-center gap-2">
                <span>📂 Category</span>
                {!p.category_slug && <span className="text-[11px] font-bold text-amber-500 uppercase tracking-wider">⚠ pick one</span>}
              </label>
              <select
                value={p.category_slug || ''}
                onChange={e => set('category_slug', e.target.value)}
                className={`w-full px-3 py-2.5 rounded border text-sm font-semibold ${inpBg}`}
              >
                <option value="">— select a category —</option>
                {cats.map(c => (
                  <option key={c.slug} value={c.slug}>{c.icon ? c.icon + ' ' : ''}{c.name}</option>
                ))}
              </select>
              <p className={`text-[11px] mt-2 ${muted}`}>
                {cats.length === 0
                  ? <>No categories yet. Add one in <b>Admin → Blog → Categories</b> first.</>
                  : <>Picks which <b>Destinations</b> card the post appears under on /blog, and which category pill is highlighted on the post page.</>
                }
              </p>
            </div>
          </Section>

          {/* Meta strip: author avatar + toggle for the trust row below the title */}
          <Section
            title="🪪 Meta strip (author · date · read time)"
            cardBg={cardBg}
            border={border}
            visible={p.show_meta_strip !== false}
            onToggle={v => set('show_meta_strip', v)}
          >
            <Field label="Author avatar URL (optional — initials shown if empty)" v={p.author_avatar_url} onChange={v => set('author_avatar_url', v)} inpBg={inpBg} />
            <button
              type="button"
              onClick={() => setImagePickerTarget('avatar')}
              className="mb-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              📁 Pick avatar from Image Manager
            </button>
            <div className="text-[11px] opacity-70 mt-1">Date + reading time are computed automatically.</div>
          </Section>

          {/* Boarding pass */}
          <Section title="✈ Boarding pass" cardBg={cardBg} border={border}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="FROM city" v={p.from_city} onChange={v => set('from_city', v)} inpBg={inpBg} />
              <Field label="TO city" v={p.to_city} onChange={v => set('to_city', v)} inpBg={inpBg} />
              <Field label="Flight number" v={p.flight_no} onChange={v => set('flight_no', v)} inpBg={inpBg} mono />
              <Field label="Seat" v={p.seat} onChange={v => set('seat', v)} inpBg={inpBg} mono />
              <Field label="Gate" v={p.gate} onChange={v => set('gate', v)} inpBg={inpBg} mono />
              <Field label="Peek mascot (emoji on card)" v={p.peek_mascot} onChange={v => set('peek_mascot', v)} inpBg={inpBg} />
            </div>
          </Section>

          {/* Cover image — used on the SINGLE POST page boarding-pass header */}
          <Section title="📷 Post-page boarding-pass image (single post header)" cardBg={cardBg} border={border} visible={p.cover_visible} onToggle={v => set('cover_visible', v)}>
            <Field label="Image URL (used on /blog/{slug} — the boarding-pass behind ★ DARIJAPRO JOURNEYS, title, From/To)" v={p.cover_image_url} onChange={v => set('cover_image_url', v)} inpBg={inpBg} />
            <button
              type="button"
              onClick={() => setImagePickerTarget('cover')}
              className="mb-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              📁 Pick from Image Manager
            </button>
            {p.cover_image_url && (
              <div className="mb-2">
                <img src={p.cover_image_url} alt="" className="max-h-32 rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <Field label="Caption (handwritten line over the photo)" v={p.cover_caption} onChange={v => set('cover_caption', v)} inpBg={inpBg} />
          </Section>

          {/* Home page ticket card image — separate from the boarding-pass cover */}
          <Section title="🎫 Home-page ticket card image (/blog grid)" cardBg={cardBg} border={border}>
            <Field label="Image URL — used on the /blog grid ticket. Leave blank to fall back to the post-page cover image above." v={p.card_image_url} onChange={v => set('card_image_url', v)} inpBg={inpBg} />
            <button
              type="button"
              onClick={() => setImagePickerTarget('card')}
              className="mb-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              📁 Pick from Image Manager
            </button>
            {p.card_image_url && (
              <div className="mb-2">
                <img src={p.card_image_url} alt="" className="max-h-32 rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                <button
                  type="button"
                  onClick={() => set('card_image_url', '')}
                  className="ml-2 text-[11px] text-red-500 hover:underline align-top"
                >
                  Clear (use post cover instead)
                </button>
              </div>
            )}
          </Section>

          {/* Post-page intro cover figure (the big photo BELOW the intro paragraphs) */}
          <Section title="🌅 Post-page cover figure (below the intro)" cardBg={cardBg} border={border}>
            <Field label="Image URL — shown as a full-width figure below the opening narrative. Leave blank to fall back to the boarding-pass image above." v={p.intro_image_url} onChange={v => set('intro_image_url', v)} inpBg={inpBg} />
            <button
              type="button"
              onClick={() => setImagePickerTarget('intro')}
              className="mb-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              📁 Pick from Image Manager
            </button>
            {p.intro_image_url && (
              <div className="mb-2">
                <img src={p.intro_image_url} alt="" className="max-h-32 rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                <button
                  type="button"
                  onClick={() => set('intro_image_url', '')}
                  className="ml-2 text-[11px] text-red-500 hover:underline align-top"
                >
                  Clear (use boarding-pass image instead)
                </button>
              </div>
            )}
          </Section>

          {/* Mascot bubble */}
          <Section title="🧑‍✈️ Youssef speech bubble" cardBg={cardBg} border={border} visible={p.mascot_visible} onToggle={v => set('mascot_visible', v)}>
            <Field label="Bubble text (supports basic HTML)" v={p.mascot_bubble_text} onChange={v => set('mascot_bubble_text', v)} inpBg={inpBg} multiline />
            <Field label="Signature line" v={p.mascot_signature} onChange={v => set('mascot_signature', v)} inpBg={inpBg} />
          </Section>

          {/* Sections */}
          <Section title="📝 Opening narrative" cardBg={cardBg} border={border} visible={p.section_intro.visible} onToggle={v => setNested('section_intro', 'visible', v)}>
            <Field label="Body (markdown)" v={p.section_intro.body_md} onChange={v => setNested('section_intro', 'body_md', v)} inpBg={inpBg} multiline tall />
          </Section>
          <Section title='📝 "Why these 10" section' cardBg={cardBg} border={border} visible={p.section_why_these.visible} onToggle={v => setNested('section_why_these', 'visible', v)}>
            <Field label="Heading (e.g. Why these 10 — and not the textbook ones)" v={p.section_why_these.heading} onChange={v => setNested('section_why_these', 'heading', v)} inpBg={inpBg} />
            <Field label="Body (markdown)" v={p.section_why_these.body_md} onChange={v => setNested('section_why_these', 'body_md', v)} inpBg={inpBg} multiline tall />
          </Section>

          {/* Inline image */}
          <Section title="🖼️ Inline image (between intro and first phrase board)" cardBg={cardBg} border={border} visible={p.inline_image.visible} onToggle={v => setNested('inline_image', 'visible', v)}>
            <Field label="Image URL (paste, or use the Pick button below)" v={p.inline_image.url} onChange={v => setNested('inline_image', 'url', v)} inpBg={inpBg} />
            <button
              type="button"
              onClick={() => setImagePickerTarget('inline')}
              className="mb-2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              📁 Pick from Image Manager
            </button>
            {p.inline_image.url && (
              <div className="mb-2">
                <img src={p.inline_image.url} alt="" className="max-h-32 rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <Field label="Caption" v={p.inline_image.caption} onChange={v => setNested('inline_image', 'caption', v)} inpBg={inpBg} />
          </Section>

          {/* Easter egg #1 */}
          <Section title="🫖 Easter egg #1 (between phrase groups)" cardBg={cardBg} border={border} visible={p.easter_egg_1.visible} onToggle={v => setNested('easter_egg_1', 'visible', v)}>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3"><Field label="Icon" v={p.easter_egg_1.icon} onChange={v => setNested('easter_egg_1', 'icon', v)} inpBg={inpBg} /></div>
              <div className="col-span-9"><Field label="Text (supports HTML)" v={p.easter_egg_1.text} onChange={v => setNested('easter_egg_1', 'text', v)} inpBg={inpBg} multiline /></div>
            </div>
          </Section>

          {/* Mid story */}
          <Section title="📝 Mid-post story break" cardBg={cardBg} border={border} visible={p.section_mid_story.visible} onToggle={v => setNested('section_mid_story', 'visible', v)}>
            <Field label="Heading" v={p.section_mid_story.heading} onChange={v => setNested('section_mid_story', 'heading', v)} inpBg={inpBg} />
            <Field label="Body (markdown)" v={p.section_mid_story.body_md} onChange={v => setNested('section_mid_story', 'body_md', v)} inpBg={inpBg} multiline tall />
          </Section>

          {/* Pull quote */}
          <Section title="💬 Pull quote (dark callout)" cardBg={cardBg} border={border} visible={p.pull_quote.visible} onToggle={v => setNested('pull_quote', 'visible', v)}>
            <Field label="Quote text" v={p.pull_quote.text} onChange={v => setNested('pull_quote', 'text', v)} inpBg={inpBg} multiline />
            <Field label="Attribution (e.g. — overheard in Fez)" v={p.pull_quote.attribution} onChange={v => setNested('pull_quote', 'attribution', v)} inpBg={inpBg} />
          </Section>

          {/* Easter egg #2 */}
          <Section title="🐪 Easter egg #2 (after phrases)" cardBg={cardBg} border={border} visible={p.easter_egg_2.visible} onToggle={v => setNested('easter_egg_2', 'visible', v)}>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3"><Field label="Icon" v={p.easter_egg_2.icon} onChange={v => setNested('easter_egg_2', 'icon', v)} inpBg={inpBg} /></div>
              <div className="col-span-9"><Field label="Text (supports HTML)" v={p.easter_egg_2.text} onChange={v => setNested('easter_egg_2', 'text', v)} inpBg={inpBg} multiline /></div>
            </div>
          </Section>

          {/* Cultural notes */}
          <Section title="🌙 Cultural notes section" cardBg={cardBg} border={border} visible={p.section_cultural_notes.visible} onToggle={v => setNested('section_cultural_notes', 'visible', v)}>
            <Field label="Heading" v={p.section_cultural_notes.heading} onChange={v => setNested('section_cultural_notes', 'heading', v)} inpBg={inpBg} />
            <Field label="Body (markdown)" v={p.section_cultural_notes.body_md} onChange={v => setNested('section_cultural_notes', 'body_md', v)} inpBg={inpBg} multiline tall />
          </Section>

          {/* Conclusion */}
          <Section title="🏁 Conclusion section" cardBg={cardBg} border={border} visible={p.section_conclusion.visible} onToggle={v => setNested('section_conclusion', 'visible', v)}>
            <Field label="Heading" v={p.section_conclusion.heading} onChange={v => setNested('section_conclusion', 'heading', v)} inpBg={inpBg} />
            <Field label="Body (markdown)" v={p.section_conclusion.body_md} onChange={v => setNested('section_conclusion', 'body_md', v)} inpBg={inpBg} multiline tall />
          </Section>

          {/* Phrases */}
          <Section title={`🧳 Phrases (${(p.phrases || []).length}/~10)`} cardBg={cardBg} border={border}>
            {(p.phrases || []).map((ph, i) => (
              <div key={i} className={`mb-3 p-3 rounded border ${border} relative`}>
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <button onClick={() => movePhrase(i, -1)} className={`text-xs ${muted} hover:text-blue-500 px-1`} title="Move up">↑</button>
                  <button onClick={() => movePhrase(i, +1)} className={`text-xs ${muted} hover:text-blue-500 px-1`} title="Move down">↓</button>
                  <label className="text-xs flex items-center gap-1 mr-1"><input type="checkbox" checked={!!ph.visible} onChange={e => setPhrase(i, 'visible', e.target.checked)} className="w-3 h-3" /> show</label>
                  <button onClick={() => removePhrase(i)} className="text-xs text-red-500 hover:underline" title="Remove">✕</button>
                </div>
                <div className={`text-xs font-bold mb-2 ${muted}`}>#{i + 1}</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input value={ph.english || ''} onChange={e => setPhrase(i, 'english', e.target.value)} placeholder="English" className={`px-2 py-1.5 rounded border text-sm ${inpBg}`} />
                  <input value={ph.latin || ''} onChange={e => setPhrase(i, 'latin', e.target.value)} placeholder="Latin (Salam)" className={`px-2 py-1.5 rounded border text-sm ${inpBg}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={ph.arabic || ''} onChange={e => setPhrase(i, 'arabic', e.target.value)} placeholder="Arabic (سلام)" dir="rtl" className={`px-2 py-1.5 rounded border text-sm ${inpBg}`} />
                  <input value={ph.note || ''} onChange={e => setPhrase(i, 'note', e.target.value)} placeholder="Optional note" className={`px-2 py-1.5 rounded border text-sm ${inpBg}`} />
                </div>
              </div>
            ))}
            <button onClick={addPhrase} className="w-full py-2 rounded border border-dashed text-sm text-blue-500 hover:bg-blue-500/10">+ Add phrase</button>
          </Section>

          {/* CTA */}
          <Section title="📣 Bottom CTA" cardBg={cardBg} border={border} visible={p.cta.visible} onToggle={v => setNested('cta', 'visible', v)}>
            <Field label="Small label (above headline)" v={p.cta.label} onChange={v => setNested('cta', 'label', v)} inpBg={inpBg} />
            <Field label="Headline" v={p.cta.headline} onChange={v => setNested('cta', 'headline', v)} inpBg={inpBg} />
            <Field label="Subtitle" v={p.cta.subtitle} onChange={v => setNested('cta', 'subtitle', v)} inpBg={inpBg} />
            <Field label="Button label" v={p.cta.button_label} onChange={v => setNested('cta', 'button_label', v)} inpBg={inpBg} />
            <Field label="Button URL" v={p.cta.button_url} onChange={v => setNested('cta', 'button_url', v)} inpBg={inpBg} />
          </Section>

          {/* Credits & attributions (renders at the very bottom of the post page) */}
          <Section
            title="✦ Credits & attributions (bottom of post)"
            cardBg={cardBg}
            border={border}
            visible={p.credits?.visible}
            onToggle={v => setNested('credits', 'visible', v)}
          >
            <Field label="Heading" v={p.credits?.heading || ''} onChange={v => setNested('credits', 'heading', v)} inpBg={inpBg} />
            <Field
              label="Body (markdown — use '- ' for bulleted lines so they render as the ✦ list)"
              v={p.credits?.body_md || ''}
              onChange={v => setNested('credits', 'body_md', v)}
              inpBg={inpBg}
              multiline
              tall
            />
          </Section>

          {/* Meta / SEO */}
          <Section title="🔍 SEO meta" cardBg={cardBg} border={border}>
            <Field label="Meta description (~150-160 chars)" v={p.meta_description} onChange={v => set('meta_description', v)} inpBg={inpBg} multiline />
            <Field label="Meta keywords (comma-separated, optional)" v={p.meta_keywords} onChange={v => set('meta_keywords', v)} inpBg={inpBg} />
          </Section>
        </div>

        {/* ── LIVE PREVIEW ── */}
        <div className={`lg:col-span-3 ${isDarkMode ? 'bg-black' : 'bg-gray-100'}`}>
          {currentSlug ? (
            <iframe
              ref={previewRef}
              src={`/blog/${currentSlug}?preview=1`}
              title="Live preview"
              className="w-full"
              style={{ height: '85vh', border: 'none' }}
            />
          ) : (
            <div className={`flex items-center justify-center h-full ${muted} text-center p-10`}>
              <div>
                <div className="text-5xl mb-4">✈</div>
                <p className="font-bold mb-2">Save first to see live preview</p>
                <p className="text-sm">Enter a title and click <strong>Save</strong> — the post renders here in real time after each save.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Manager picker — opens when admin clicks the 📁 Pick button on cover/inline */}
      <AdminImagePicker
        open={!!imagePickerTarget}
        onClose={() => setImagePickerTarget(null)}
        onPick={(asset) => {
          const url = asset?.url || '';
          if (!url) return;
          if (imagePickerTarget === 'cover') set('cover_image_url', url);
          else if (imagePickerTarget === 'card') set('card_image_url', url);
          else if (imagePickerTarget === 'intro') set('intro_image_url', url);
          else if (imagePickerTarget === 'inline') setNested('inline_image', 'url', url);
          else if (imagePickerTarget === 'avatar') set('author_avatar_url', url);
        }}
        API={API.replace(/\/api\/?$/, '')}
        authHeaders={authHeaders}
        title="Pick a blog image"
      />
    </div>
  );
}

// ── Re-usable building blocks ──────────────────────────────────────────────

function Section({ title, children, cardBg, border, visible, onToggle }) {
  return (
    <div className={`mb-5 p-4 rounded-lg border ${border} ${cardBg}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider opacity-80">{title}</h3>
        {onToggle && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!visible} onChange={e => onToggle(e.target.checked)} className="w-4 h-4" />
            <span className="opacity-70">show in post</span>
          </label>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, v, onChange, inpBg, multiline, tall, mono }) {
  const fontCls = mono ? 'font-mono' : '';
  return (
    <div className="mb-2.5">
      <label className="text-xs font-semibold opacity-80 mb-1 block">{label}</label>
      {multiline ? (
        <textarea
          value={v || ''}
          onChange={e => onChange(e.target.value)}
          rows={tall ? 8 : 2}
          className={`w-full px-3 py-2 rounded border text-sm ${fontCls} ${inpBg}`}
        />
      ) : (
        <input
          value={v || ''}
          onChange={e => onChange(e.target.value)}
          className={`w-full px-3 py-2 rounded border text-sm ${fontCls} ${inpBg}`}
        />
      )}
    </div>
  );
}
